import { useState } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { ArrowDownCircle, ArrowUpCircle, Loader2, Users, X, CheckCircle2, AlertTriangle } from 'lucide-react';

const fmt = (a, c) => `${c === 'USD' ? '$' : '€'}${Number(a || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const BulkSelectionBar = ({ count, onClear, onCredit, onDebit }) => {
    if (!count) return null;
    return (
        <div className="sticky top-2 z-30 rounded-2xl bg-[#0b2a4a]/95 backdrop-blur border border-[#1973B8]/50 px-4 py-3 flex flex-wrap items-center justify-between gap-3 shadow-xl" data-testid="bulk-selection-bar">
            <div className="flex items-center gap-2 text-white text-sm font-semibold">
                <Users className="w-4 h-4 text-[#7CB1E5]" /> <span data-testid="bulk-selected-count">{count}</span> usuario{count !== 1 ? 's' : ''} seleccionado{count !== 1 ? 's' : ''}
                <button type="button" onClick={onClear} className="ml-2 text-slate-400 hover:text-white inline-flex items-center gap-1 text-xs font-normal" data-testid="bulk-clear-selection"><X className="w-3.5 h-3.5" /> Limpiar</button>
            </div>
            <div className="flex gap-2">
                <Button size="sm" onClick={onCredit} className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-9" data-testid="bulk-credit-btn">
                    <ArrowDownCircle className="w-4 h-4 mr-1.5" /> Agregar saldo a {count}
                </Button>
                <Button size="sm" onClick={onDebit} className="bg-red-500/90 hover:bg-red-600 text-white font-semibold h-9" data-testid="bulk-debit-btn">
                    <ArrowUpCircle className="w-4 h-4 mr-1.5" /> Debitar a {count}
                </Button>
            </div>
        </div>
    );
};

export const BulkBalanceDialog = ({ mode, users: usersProp, open, onOpenChange, onDone }) => {
    const isCredit = mode === 'credit';
    const [users] = useState(usersProp);
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('EUR');
    const [note, setNote] = useState('');
    const [notify, setNotify] = useState(true);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const reset = () => { setAmount(''); setNote(''); setResult(null); setNotify(true); };
    const close = (o) => { if (!o) reset(); onOpenChange(o); };

    const run = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error('Indica un importe mayor que 0'); return; }
        if (!isCredit && note.trim().length < 3) { toast.error('El motivo del débito es obligatorio'); return; }
        setBusy(true);
        try {
            const ids = users.map((u) => u.id);
            const { data } = isCredit
                ? await api.post('/admin/zero-balance-users/add-balance', { user_ids: ids, amount: amt, currency, description: note.trim() || null, notify_email: notify })
                : await api.post('/admin/users/bulk-debit', { user_ids: ids, amount: amt, currency, reason: note.trim(), notify_user: notify });
            setResult(data);
            const okCount = isCredit ? (data.credited ?? ids.length) : data.debited_count;
            if (okCount > 0) toast.success(isCredit ? `Saldo agregado a ${okCount} usuarios` : `Débito aplicado a ${okCount} usuarios`);
            else toast.error('No se pudo aplicar el débito a ningún usuario seleccionado');
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo completar la operación');
        } finally { setBusy(false); }
    };

    const total = (parseFloat(amount) || 0) * users.length;
    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-lg bg-slate-900 border-slate-700 text-white" data-testid={`bulk-${mode}-dialog`}>
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        {isCredit ? <ArrowDownCircle className="w-5 h-5 text-emerald-400" /> : <ArrowUpCircle className="w-5 h-5 text-red-400" />}
                        {isCredit ? 'Agregar saldo simultáneo' : 'Débito simultáneo'} · {users.length} usuarios
                    </DialogTitle>
                </DialogHeader>
                {result ? (
                    <div className="space-y-3 py-1" data-testid={`bulk-${mode}-result`}>
                        {(isCredit ? (result.credited ?? users.length) : result.debited_count) > 0 ? (
                            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3 flex items-start gap-2">
                                <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                                <p className="text-sm text-slate-200">
                                    {isCredit ? `Saldo agregado a ${result.credited ?? users.length} usuarios` : `Débito aplicado a ${result.debited_count} usuarios`}
                                    {' '}por {fmt(parseFloat(amount), currency)} {currency} cada uno · Remitente: CENTRAL BANK OF CYPS
                                </p>
                            </div>
                        ) : (
                            <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-3 flex items-start gap-2" data-testid={`bulk-${mode}-none`}>
                                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                                <p className="text-sm text-red-100">No se aplicó a ningún usuario. Revisa los motivos a continuación.</p>
                            </div>
                        )}
                        {!isCredit && result.failed_count > 0 && (
                            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3" data-testid="bulk-debit-failed">
                                <p className="text-xs text-amber-200 flex items-center gap-1 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> {result.failed_count} omitidos (fondos insuficientes u otro motivo):</p>
                                <div className="max-h-32 overflow-y-auto text-[11px] text-slate-300 space-y-0.5">
                                    {result.failed.map((f) => <p key={f.user_id}>{f.name} · {f.email} — <span className="text-amber-300">{f.reason}</span></p>)}
                                </div>
                            </div>
                        )}
                        <DialogFooter><Button onClick={() => close(false)} className="bg-[#1973B8] hover:bg-[#1973B8]/85 text-white" data-testid={`bulk-${mode}-close`}>Cerrar</Button></DialogFooter>
                    </div>
                ) : (
                    <div className="space-y-4 py-1">
                        <div className="rounded-xl bg-slate-950 border border-slate-800 max-h-28 overflow-y-auto divide-y divide-slate-800/60">
                            {users.slice(0, 200).map((u) => <p key={u.id} className="px-3 py-1.5 text-xs text-slate-300 truncate">{u.name} <span className="text-slate-500">· {u.email}</span></p>)}
                            {users.length > 200 && <p className="px-3 py-1.5 text-[11px] text-slate-500">… y {users.length - 200} más</p>}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2 space-y-1.5">
                                <Label className="text-slate-400 text-xs">Importe por usuario</Label>
                                <Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="bg-slate-950 border-slate-800 text-white" data-testid={`bulk-${mode}-amount`} />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-slate-400 text-xs">Moneda</Label>
                                <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-full h-10 rounded-md bg-slate-950 border border-slate-800 text-white text-sm px-3" data-testid={`bulk-${mode}-currency`}>
                                    <option value="EUR">EUR</option><option value="USD">USD</option>
                                </select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-slate-400 text-xs">{isCredit ? 'Nota interna (opcional · no se muestra al usuario)' : 'Motivo del débito (obligatorio)'}</Label>
                            <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="bg-slate-950 border-slate-800 text-white text-sm min-h-[70px]" data-testid={`bulk-${mode}-note`} />
                        </div>
                        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                            <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} className="border-slate-600 data-[state=checked]:bg-[#1973B8]" data-testid={`bulk-${mode}-notify`} />
                            {isCredit ? 'Enviar email de abono a cada usuario' : 'Notificar a cada usuario'}
                        </label>
                        <div className={`rounded-xl border p-3 text-xs ${isCredit ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-100' : 'bg-red-500/10 border-red-500/30 text-red-100'}`} data-testid={`bulk-${mode}-summary`}>
                            {isCredit ? 'Se agregará' : 'Se debitará'} <strong>{fmt(parseFloat(amount) || 0, currency)} {currency}</strong> a cada uno de los <strong>{users.length}</strong> usuarios · Total: <strong>{fmt(total, currency)} {currency}</strong>.
                            {isCredit ? ' Concepto: TRANSFERENCIA BANCARIA A FAVOR DE [NOMBRE] · Remitente: CENTRAL BANK OF CYPS.' : ' Los usuarios sin fondos suficientes se omiten y se listan al final.'}
                        </div>
                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => close(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancelar</Button>
                            <Button onClick={run} disabled={busy || !amount} className={isCredit ? 'bg-emerald-500 hover:bg-emerald-600 text-white font-semibold' : 'bg-red-500 hover:bg-red-600 text-white font-semibold'} data-testid={`bulk-${mode}-submit`}>
                                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Confirmar {isCredit ? 'abono' : 'débito'} a {users.length}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
